import { type ReactNode, type RefObject, useLayoutEffect, useRef } from 'react';
import { Dialog, Modal as AriaModal, ModalOverlay, Button } from 'react-aria-components';
import Delete1Icon from '@streamlinehq/streamlinehq/img/streamline/delete-1.svg';
import styles from './Modal.module.css';

export type Props = {
  isOpen?: boolean;
  onClose?: () => void;
  children?: ReactNode;
  maxInlineSize?: `${number}rem` | 'max-content';
  maxBlockSize?: `${number}rem` | `${number}vh`;
  'aria-label'?: string;
};

export function Modal({
  isOpen,
  onClose,
  children,
  maxInlineSize = '31.25rem',
  maxBlockSize = '95vh',
  'aria-label': ariaLabel,
}: Props) {
  const dialogRef = useRef<HTMLElement>(null);
  return (
    // The overlay is portalled to `document.body`, so it escapes the app's `ThemeProvider` /
    // `BaseStyles` wrapper — and that wrapper, not `:root`, is what carries Primer's functional
    // colour variables (`--bgColor-default`, `--fgColor-muted`, `--borderColor-default`,
    // `--focus-outlineColor`, ...). They are declared behind
    // `[data-color-mode="light"][data-light-theme="light"]` in both `@primer/primitives` and
    // `theme.css`, so anything inside the portal resolves them to nothing and paints unstyled,
    // silently: no error, just a control that never shows its state. Re-establishing the two
    // attributes here fixes every `Modal` call site at once instead of per portal.
    <ModalOverlay
      className={styles.overlay}
      data-color-mode="light"
      data-light-theme="light"
      isDismissable
      isOpen={isOpen}
      onOpenChange={newIsOpen => {
        if (!newIsOpen) onClose?.();
      }}
    >
      <AriaModal className={styles.modal} style={{ maxBlockSize, maxInlineSize }}>
        <Dialog ref={dialogRef} aria-label={ariaLabel} role="alertdialog" className={styles.dialog}>
          <ReturnFocusWhenOpenerIsGone dialogRef={dialogRef} />
          <Button className={styles.close} aria-label="Close modal" slot="close">
            {/* Sized by `.close > svg` in the stylesheet, beside the insets that are derived
                from it. Passing width and height here would put half of that arithmetic in a
                file that cannot see the other half. */}
            <Delete1Icon />
          </Button>
          {children}
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  );
}

/**
 * Returns focus to the menu trigger when a menu item opened the dialog.
 *
 * React Aria restores focus on close to whatever was focused when the dialog mounted. Opened from
 * a menu item (Help → Contact Upright), that is the item, and the menu unmounts in the same
 * commit, so on close there is nothing to go back to and focus drops to `<body>`: a keyboard
 * reader's next Tab starts from the top of the page. On its way out the menu has already moved
 * focus to its trigger (Primer's `ActionMenu` and React Aria's `MenuTrigger` both do), which is
 * where the reader expects to land, and that focus is pulled into the dialog a moment later.
 *
 * So this remembers the first element outside the dialog that takes focus while the dialog is
 * opening, and focuses it on close if React Aria left focus on `<body>`. A dialog opened from a
 * plain button never sees such an element, and React Aria's own restoration stands.
 *
 * Rendered inside the `Dialog` so that it mounts and unmounts with each opening. The unmount
 * check runs a frame later, after React Aria's own attempt, which is scheduled the same way.
 */
function ReturnFocusWhenOpenerIsGone({ dialogRef }: { dialogRef: RefObject<HTMLElement | null> }) {
  useLayoutEffect(() => {
    let fallback: HTMLElement | null = null;
    const onFocusIn = (event: FocusEvent) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }
      if (dialogRef.current?.contains(event.target)) {
        document.removeEventListener('focusin', onFocusIn, true);
        return;
      }
      fallback ??= event.target;
    };
    // Capture phase, so this sees the trigger's focus before React Aria's containment moves it.
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      const target = fallback;
      if (!target) {
        return;
      }
      requestAnimationFrame(() => {
        if (document.activeElement === document.body && target.isConnected) {
          target.focus();
        }
      });
    };
  }, [dialogRef]);
  return null;
}
